// src/components/VarianceUnmatch.js
import React, { useState, useEffect } from 'react';

// Format "YYYY-MM-DD" → "02 Jan 2025" without TZ surprises
function formatDate(dateString) {
  if (!dateString) return '';
  const s = String(dateString);
  const iso = s.includes('T') ? s.split('T')[0] : s; // "YYYY-MM-DD"
  const [y, m, d] = iso.split('-');
  if (y && m && d) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d} ${months[Number(m) - 1]} ${y}`;
  }
  try {
    return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ');
  } catch {
    return '';
  }
}

export default function VarianceUnmatch() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editIdx, setEditIdx] = useState(-1);
  const [editRow, setEditRow] = useState({});

  // load on mount
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError('');
    fetch('/api/variance-unmatch', { signal: ac.signal, cache: 'no-store' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(err => { if (err.name !== 'AbortError') setError(err.message || 'Failed to load'); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, []);

  const handleEdit   = (idx) => { setEditIdx(idx); setEditRow({ ...rows[idx] }); };
  const handleCancel = ()     => { setEditIdx(-1);  setEditRow({}); setError(''); };
  const handleChange = (f, v) => setEditRow(pr => ({ ...pr, [f]: v }));

  async function handleSave() {
    if (editIdx < 0) return;
    setSaving(true);
    setError('');

    // Some views expose CustomBCNo instead of BCNo — support both
    const bcno = editRow.CustomBCNo ?? editRow.BCNo;
    if (!bcno) {
      setError('Missing BC number on the selected row.');
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`/api/variance-unmatch/${encodeURIComponent(bcno)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          RemarksIT:   editRow.RemarksIT   ?? null,
          RemarksWH:   editRow.RemarksWH   ?? null,
          RemarksEXIM: editRow.RemarksEXIM ?? null
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      // Re-fetch to keep computed/view fields in sync with DB
      const fresh = await fetch('/api/variance-unmatch', { cache: 'no-store' }).then(r => r.json());
      setRows(Array.isArray(fresh) ? fresh : []);
      handleCancel();
    } catch (err) {
      setError(err.message || 'Failed to update remarks');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 18 }}>
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>Variance – Unmatch</h1>
      {error && <div style={{ color: '#d9534f', fontSize: 13, marginBottom: 8 }}>{error}</div>}

      <div style={{ background: '#f8faff', borderRadius: 8, marginTop: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: 1400 }}>
          <thead>
            <tr>
              <th style={th}>S. No</th>
              <th style={th}>Supplier</th>
              <th style={th}>Requester</th>
              <th style={th}>Department</th>
              <th style={th}>Date</th>
              <th style={th}>Form Type</th>
              <th style={th}>BC No</th>
              <th style={th}>AJU No</th>
              <th style={th}>BC Date</th>
              <th style={th}>GRN Qty</th>
              <th style={th}>Chesa Qty</th>
              <th style={th}>Remarks (IT)</th>
              <th style={th}>Remarks (WH)</th>
              <th style={th}>Remarks (Exim)</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} style={td}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={15} style={td}>No data found.</td></tr>
            ) : rows.map((row, idx) => {
              const bcno = row.CustomBCNo ?? row.BCNo;
              return (
                <tr key={`${bcno ?? 'row'}-${idx}`}>
                  <td style={td}>{idx + 1}</td>
                  <td style={td}>{row.Supplier}</td>
                  <td style={td}>{row.RequesterName || ''}</td>
                  <td style={td}>{row.RequesterDepartment || ''}</td>
                  <td style={td}>{formatDate(row.RequestedDate)}</td>
                  <td style={td}>{row.FormType}</td>
                  <td style={td}>{bcno}</td>
                  <td style={td}>{row.AJUNo}</td>
                  <td style={td}>{formatDate(row.BCDate)}</td>
                  <td style={td}>{row.GRNQty}</td>
                  <td style={td}>{row.ChesaQty}</td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        type="text"
                        value={editRow.RemarksIT || ''}
                        onChange={e => handleChange('RemarksIT', e.target.value)}
                        style={input}
                      />
                    ) : (row.RemarksIT || '')}
                  </td>
                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        type="text"
                        value={editRow.RemarksWH || ''}
                        onChange={e => handleChange('RemarksWH', e.target.value)}
                        style={input}
                      />
                    ) : (row.RemarksWH || '')}
                  </td>
                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        type="text"
                        value={editRow.RemarksEXIM || ''}
                        onChange={e => handleChange('RemarksEXIM', e.target.value)}
                        style={input}
                      />
                    ) : (row.RemarksEXIM || '')}
                  </td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <>
                        <button style={saveBtn} onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button style={cancelBtn} onClick={handleCancel} disabled={saving}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button style={editBtn} onClick={() => handleEdit(idx)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = {
  padding: '7px 8px',
  background: '#f8faff',
  borderBottom: '1px solid #eee',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontSize: '13px'
};
const td = {
  padding: '7px 8px',
  borderBottom: '1px solid #eee',
  background: '#fff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 140
};
const input = {
  fontSize: '12px',
  padding: '2px 5px',
  width: '110px',
  borderRadius: 4,
  border: '1px solid #bbb'
};
const editBtn = {
  fontSize: '12px',
  padding: '3px 10px',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
const saveBtn = {
  fontSize: '12px',
  padding: '3px 7px',
  marginRight: 3,
  background: '#22c55e',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
const cancelBtn = {
  fontSize: '12px',
  padding: '3px 7px',
  background: '#f87171',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
